import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subject, Subscription } from 'rxjs';

import { BaseWizardService } from '../base.wizard.service';
import { SitesWizardStep } from './sites.wizard.step';
import {
  ApiService,
  InputDatastoreService,
  DialogService,
  CountryListService,
  ValuesExchangeProvider,
  SitesService
} from '@nrwl/common-lib';
import { takeUntil, first } from 'rxjs/operators';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  cloneDeep,
  filter,
  forEach,
  get,
  isEmpty,
  isObject,
  isString,
  last,
  map,
  size,
  uniq,
  unset
} from 'lodash';

@Injectable()
export class SitesWizardService extends BaseWizardService implements OnDestroy {
  unsubscribe: Subject<void> = new Subject();

  subContentDataSourceChangeEvent: Subject<void> = new Subject();

  openNew: boolean;
  state: ActivatedRoute;
  currentParam: any; // eslint-disable-line
  routeSubscriber: Subscription;
  showUploadSites: boolean;
  formattedSiteData: any; // eslint-disable-line
  uploadDataObj: any; // eslint-disable-line
  uploadSitesURL = '/entity/v1/sites/load';
  lastSubmittedOutput: any; // eslint-disable-line
  lastSubmittedParams: any; // eslint-disable-line

  successSiteList: any; // eslint-disable-line
  constructor(
    apiService: ApiService,
    state: ActivatedRoute,
    inputDatastoreService: InputDatastoreService,
    countryListService: CountryListService,
    dialogService: DialogService,
    private readonly http: HttpClient,
    private readonly exchange: ValuesExchangeProvider,
    private sitesService: SitesService
  ) {
    super(apiService, inputDatastoreService, countryListService, dialogService);
    this.state = state;
    this.stepClass = SitesWizardStep;
    this.openNew = false;
    this.successSiteList = null;

    this.routeSubscriber = this.state.params.subscribe(params => {
      this.currentParam = params.tests;
    });
  }

  ngOnDestroy() {
    if (this.routeSubscriber) {
      this.routeSubscriber.unsubscribe();
      this.routeSubscriber = null;
    }
  }

  //------------------------------------------------ Init config/data ----------------------------------------------------

  openWizard(
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    wizardId: any, // eslint-disable-line
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    wizardConfig: any, // eslint-disable-line
    defaultParamsById = {},
    onSuccess = () => {},
    onFail = () => {}
  ): void {
    const siteId = get(defaultParamsById, 'id');
    super.openWizard(wizardId, wizardConfig, defaultParamsById, onSuccess, onFail);
    if (this.editMode) {
      this.finishStep = 'UPDATE';
      this.showUploadSites = false;
      this.dataUrl = `${this.dataUrl}/${siteId}`;
      this._getDataForEdit(siteId);
      this.currentStep = last(this.steps);
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  _setDefaultParams(step: any, targetOutput: any, defaultParamsById: any = {}) {
    // eslint-disable-line
    super._setDefaultParams(step, targetOutput, defaultParamsById);
  }

  //------------------------------------------------ Wizard control ----------------------------------------------------

  // eslint-disable-next-line
  onFinish(): any {
    this.openNew = false;
    this.currentStep.onNext();
    this.dirtyFlag = false;

    let responseProcessed;

    if (this.showUploadSites) {
      this.dataUrl = this.uploadSitesURL;

      const isHeaderPresent = this.uploadDataObj.isHeaderPresent;
      const params = { withHeader: isHeaderPresent };

      this.lastSubmittedParams = cloneDeep(params);

      responseProcessed = this._uploadSitesInCSV(this.uploadDataObj.csvData, params, this.openNew);
    } else {
      const output = this._getFormatOutput();
      this.lastSubmittedOutput = cloneDeep(output);

      responseProcessed = this._saveRequest(output, null);
    }

    responseProcessed.subscribe(result => {
      this.exchange.notifyListeners('new_site', result);
      // all done, the only thing we need -> to move over to finishing step.
      this.currentStep = this.finishingStep;
    });

    // we need to inform new.application.wizard about results of operation, so that
    // it can create the component, representing the finishingStep object.
    return responseProcessed;
  }

  onFinishOpenNew(): void {
    this.openNew = true;
    this._saveWizardData(true);
  }

  // eslint-disable-next-line
  retrySaveRequest(): Observable<any> {
    if (this.showUploadSites) {
      return this._uploadSitesInCSV(this.uploadDataObj.csvData, this.lastSubmittedParams);
    } else {
      return this._saveRequest(this.lastSubmittedOutput, null);
    }
  }

  setNextStep(): void {
    if (this.currentStep.id === 'wizardService.add_sites.site_desc' && this.showUploadSites) {
      // move to 'finish' step
      this.currentStep = this.steps[this.steps.length - 1];
      this.currentStep.selectTypeContent('uploadSiteDetails', this.formattedSiteData[0]);
    } else {
      super.setNextStep();
    }
  }

  nextStep(): void {
    super.nextStep();
  }

  _setPreviousStep(): void {
    if (this.currentStep.id === 'wizardService.add_sites.site_create' && this.showUploadSites) {
      // move to 'beginning' step
      this.currentStep = this.steps[0];
    } else {
      super._setPreviousStep();
    }
  }

  showDatasource(): boolean {
    return !!this.output.source;
  }

  // Formatter
  // There is situation on create_new site wizard, when source output
  // is defined in formatter, but it is optional property
  // So formatter breaks code when it tries to add property on not defined source (source = null)
  // eslint-disable-next-line
  _getFormatOutput(output: any = this.output, format: any = this.formatData): any {
    const formatedOutput = {};
    forEach(format, (param, key) => {
      if (!isEmpty(param)) {
        formatedOutput[key] = this._getFormatOutputItem(output[key], param);
      } else {
        formatedOutput[key] = output[key];
      }
    });
    return formatedOutput;
  }

  // eslint-disable-next-line
  _getFormatOutputItem(output: any, format: any): any {
    let newOutput = {};
    if (format.type === 'array') {
      newOutput = map(output, function (item) {
        let newItem = {};

        if (format.param) {
          newItem = item[format.param];
        } else {
          forEach(format.params, function (key) {
            newItem[key] = item[key];
          });
        }
        return newItem;
      });
    } else if (format.type === 'object') {
      // New rule to ignore formatter if data is not defined
      if (isEmpty(output)) {
        return newOutput;
      }

      forEach(format.params, function (item) {
        //TODO fix it
        // eslint-disable-next-line
        const itemTempFix = item as any; // @TODO: warning - this is not a fix, but a patch to bypass the build erro
        if (isObject(item)) {
          const values = output[itemTempFix.value];
          forEach(values, value => unset(value, 'selected'));
          newOutput[itemTempFix.key] = values;
        } else {
          newOutput[item] = output[item];
        }
      });
    } else if (format.type === 'key:value') {
      newOutput = map(output, item => {
        return `${item.key}:${item.value}`;
      });
    } else {
      newOutput = output[format.param];
    }
    return newOutput;
  }

  //--------------------------------------------- Backend response -------------------------------------------------------

  //--- Added Seperate API method call in-order to handle formData,
  //    To handle upload success or failure dialog message
  //    TO-DO - use api.service
  //   TODO - subscribers need to be removed and added it in component
  // eslint-disable-next-line
  _uploadSitesInCSV(output: any, params: any, openOnFinish?: boolean): Observable<any> {
    this.isLoading = true;
    this.requestFailed = false;
    this.errorMessage = null;
    this.successSiteList = null;
    this.currentStep.onNext();

    let reqParams: HttpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach(function (key) {
        reqParams = reqParams.append(key, params[key]);
      });
    }

    const responseProcessed: Subject<any> = new Subject(); // eslint-disable-line
    // eslint-disable-next-line
    const request = this.http['put']<any>(this.dataUrl, output, { params: reqParams }).pipe(
      takeUntil(this.unsubscribe)
    );

    request.subscribe(
      response => {
        this.isLoading = false;

        if (!this.finishingStep) {
          this.onSuccess(response.success, this.output);
        }

        this.successSiteList = map(filter(response, 'success'), rez => {
          return rez.success;
        });
        const errorSiteList = filter(response, 'errors');

        if (errorSiteList.length === 0 && this.successSiteList.length > 0) {
          this.formatData = null;
          this.dataSaved = true;
        } else {
          this.requestFailed = true;
          const errObj = this.uploadCsvFileErrorHandler(errorSiteList);
          this.errorMessage = JSON.stringify(errObj);
        }

        responseProcessed.next({});
        responseProcessed.complete();
      },
      error => {
        this.isLoading = false;
        this.requestFailed = true;

        if (error.status === 403 || error.status === 401 || error.status === 500) {
          this.errorMessage = null;
        } else {
          const errObj = this.uploadCsvFileErrorHandler(get(error, 'error', []));
          this.errorMessage = JSON.stringify(errObj);
        }
        responseProcessed.next({});
        responseProcessed.complete();
      },
      () => {}
    );
    return responseProcessed.pipe(first());
  }

  // eslint-disable-next-line
  uploadCsvFileErrorHandler(errors: any): any {
    return this.populateErrorDialogMessage(errors);
  }

  // eslint-disable-next-line
  _saveRequest(output: any, openOnFinish?: boolean) {
    this.isLoading = true;
    this.requestFailed = false;
    this.errorMessage = null;
    // eslint-disable-next-line
    const request = this.apiService[this.method + '$']<any>(this.dataUrl, output).pipe(
      takeUntil(this.unsubscribe)
    );
    // we want to be able to notify the top level angular component about end of request.
    // if we return 'request' itself, then there is no guarantee which subscriber will run first (hence
    // there is a risk that your listener/angular component will be notified sooner than base.wizard.service
    // updates inner state variables). Hence a separate Subject object.
    const responseProcessed: Subject<any> = new Subject(); // eslint-disable-line

    request.subscribe(
      response => {
        //this.inputDatastoreService.broadcastUpdate(this.inputDatastoreService.getInputs(), false);
        if (!this.finishingStep) {
          this.onSuccess(response.success, this.output);
        }
        this.isLoading = false;
        // notice the simplicity of emited value! The reason: subscribers must use base.wizard.service state variables
        // (such as dataSaved, requestFailed, isLoading, etc) to figure out what is the outcome of the call.
        responseProcessed.next(response);
        responseProcessed.complete();
      },
      error => {
        this.isLoading = false;
        this.requestFailed = true;
        if (error.status === 409 || get(error, 'error.errors[0]', '')) {
          const parsedMessage = this.parseErrorMessage(get(error, 'error.errors[0]', ''));
          this.errorMessage = JSON.stringify(parsedMessage);
        } else {
          this._handleErrorResponse(error, this.onFail);
        }

        // see remark above on reasons for emiting a plain {} object.
        responseProcessed.next({});
        responseProcessed.complete();
      },
      () => {}
    );
    return responseProcessed.pipe(first());
  }

  // eslint-disable-next-line
  populateUploadDialogMessage(response: any): void {
    const success = filter(response, 'success');
    const errors = filter(response, 'errors');
    const successCount = size(success);
    const errorCount = size(errors);
    const errorTitle = 'Error Message';

    if (successCount && !errorCount) {
      const message = successCount + ' sites uploaded successfully';
      this.dialogService.showInfo('information', 'body', message, 'Site Upload Complete');
    } else if (successCount && errorCount) {
      const message = `${successCount} sites successfully created while ${errorCount} sites failed`;
      const tableContent = this.populateErrorDialogMessage(errors);
      this.dialogService.showInfo('information', 'body', message, errorTitle);
    } else if (!successCount && errorCount) {
      const message = errorCount + ' sites creation failed';
      const tableContent = this.populateErrorDialogMessage(errors);
      this.dialogService.showInfo('information', 'body', message, errorTitle);
    } else {
      const message = 'Problem while uploading sites';
      this.dialogService.showInfo('information', 'body', message, errorTitle);
    }
  }

  // eslint-disable-next-line
  populateErrorDialogMessage(errors: any): any {
    const tableContent: any = {
      // eslint-disable-line
      header: ['Site', 'Error'],
      title: 'Sites that failed',
      content: []
    };

    if (isString(errors)) {
      const error = {
        siteName: 'Site',
        error: errors
      };

      tableContent.content.push(error);
      return tableContent;
    }

    const content = [];
    errors.forEach(error => {
      const row = {};
      const siteName = error.failed.name;
      const errorMsg = error.errors[0];

      row['siteName'] = siteName;
      row['error'] = errorMsg;
      content.push(row);
    });

    tableContent.content = content;
    return tableContent;
  }

  // eslint-disable-next-line
  parseErrorMessage(message: string): any {
    const listOfMessages = message.split(/\[(.*?)]/);
    const filteredResult = uniq(filter(listOfMessages, item => item));

    const tableContent: any = {
      // eslint-disable-line
      header: ['Site', 'Error'],
      title: get(filteredResult, '[0]', 'Site(s) that failed:'),
      content: []
    };

    for (let i = 1; i < filteredResult.length; i++) {
      tableContent.content.push({
        siteName: filteredResult[i].toString().match(/'(.*?)'/)[1],
        error: filteredResult[i]
      });
    }

    return tableContent;
  }

  // To get data in edit mode
  // eslint-disable-next-line
  _getDataForEdit(siteId: any): void {
    // eslint-disable-line
    // eslint-disable-next-line
    const siteObject = this.sitesService.getSitesById(siteId);
    this.defaultParamsById = siteObject.sitesData;
    this._setOutputForEditMode(this.defaultParamsById);
  }
}
